import { observer } from "mobx-react";
import exchangeInterfaceStore from "../../../stores/ExchangeInterfaceStore";
import Input, { InputProps } from "./Input";
import { usePresale } from "../../../utils/hooks/presale-manager/read/usePresale";
import {
  ESTIMATED_TRANSACTION_FEES,
  PAYMENT_METHODS,
  PRESALE_ID,
} from "../../../utils/constants";
import { useLatestBNBPrice } from "../../../utils/hooks/presale-manager/read/useLatestBNBPrice";
import { resetInputValues } from "../../../utils/helpers";
import { useRef } from "react";
import { useBNBBalance } from "../../../utils/hooks/presale-manager/read/useBNBBalance";
import { useUSDTBalance } from "../../../utils/hooks/usdt-manager/read/useUSDTBalance";
import { formatEther } from "viem";

const Inputs = () => {
  const presaleData = usePresale(PRESALE_ID)?.data!;
  const bnbPrice = useLatestBNBPrice()?.bnbPrice!;
  const usdtBalance = useUSDTBalance()?.usdtBalance!;
  const rawBnbBalance = useBNBBalance()?.bnbBalance!;

  const { activePaymentMethod, inputsValues, setInputsValues, statuses } =
    exchangeInterfaceStore;

  const inputNames = {
    baseCoin: activePaymentMethod,
    pepeCoin: "PEPE3D",
  };

  const inputsData: InputProps[] = [
    {
      title: `$${activePaymentMethod} you pay`,
      iconSrc: `/assets/icons/${activePaymentMethod.toLowerCase()}.svg`,
      iconAlt: `${activePaymentMethod}_LOGO`,
      name: inputNames.baseCoin,
      value: inputsValues.baseCoinsToPay,
      enableMaxAmountButton: true,
    },
    {
      title: `$PEPE3D you receive`,
      iconSrc: "/assets/icons/pepecoin.svg",
      iconAlt: "PEPE3D_LOGO",
      name: inputNames.pepeCoin,
      value: inputsValues.pepeCoinsToReceive,
    },
  ];

  const exchangeInterfaceInputHandler = (
    inputName: string
  ): React.ChangeEventHandler<HTMLInputElement> => {
    return (event) => {
      const value = Number(event.target.value);
      if (!value && value < 0) {
        resetInputValues();
        return;
      }

      if (activePaymentMethod === "USDT") {
        if (inputName === inputNames.baseCoin) {
          setInputsValues({
            baseCoinsToPay: value,
            pepeCoinsToReceive: value / presaleData.price,
          });
          return;
        }
        if (inputName === inputNames.pepeCoin) {
          setInputsValues({
            baseCoinsToPay: value * presaleData.price,
            pepeCoinsToReceive: value,
          });
          return;
        }
        return;
      }

      if (activePaymentMethod === "BNB") {
        if (inputName === inputNames.baseCoin) {
          setInputsValues({
            baseCoinsToPay: value,
            pepeCoinsToReceive: parseFloat(
              ((value * bnbPrice) / presaleData.price).toFixed(2)
            ),
          });
          return;
        }
        if (inputName === inputNames.pepeCoin) {
          setInputsValues({
            baseCoinsToPay: (value * presaleData.price) / bnbPrice,
            pepeCoinsToReceive: value,
          });
          return;
        }
        return;
      }
    };
  };

  const maxAmountHandler = () => {
    if (statuses.WALLET_DISCONNECTED) return;
    const bnbBalance = Number(formatEther(rawBnbBalance.value));
    let maxActivePaymentMethodValue: number = 0;
    let pepeCoinsToReceive: number = 0;

    if (activePaymentMethod === PAYMENT_METHODS.BNB && bnbBalance > 0) {
      maxActivePaymentMethodValue = bnbBalance - ESTIMATED_TRANSACTION_FEES;
      if (maxActivePaymentMethodValue <= 0) return;

      pepeCoinsToReceive = parseFloat(
        ((maxActivePaymentMethodValue * bnbPrice) / presaleData.price).toFixed(
          2
        )
      );

      setInputsValues({
        baseCoinsToPay: maxActivePaymentMethodValue,
        pepeCoinsToReceive: pepeCoinsToReceive,
      });
      return;
    }

    if (activePaymentMethod === PAYMENT_METHODS.USDT && usdtBalance > 0) {
      maxActivePaymentMethodValue = usdtBalance;

      if (maxActivePaymentMethodValue <= 0) return;

      setInputsValues({
        baseCoinsToPay: maxActivePaymentMethodValue,
        pepeCoinsToReceive: maxActivePaymentMethodValue / presaleData.price,
      });
      return;
    }
  };

  return (
    <>
      {inputsData.map(
        (
          {
            title,
            iconSrc,
            iconAlt,
            name: inputName,
            value,
            enableMaxAmountButton,
          },
          index
        ) => (
          <Input
            key={`Input-${index}`}
            title={title}
            iconSrc={iconSrc}
            iconAlt={iconAlt}
            value={value}
            enableMaxAmountButton={enableMaxAmountButton}
            maxAmountHandler={maxAmountHandler}
            onChange={(event) =>
              exchangeInterfaceInputHandler(inputName!)(event)
            }
          />
        )
      )}
    </>
  );
};

export default observer(Inputs);
